import { IressIcon } from '@iress-oss/ids-components';
import type { CreditState } from '@/lib/wallet';
import './CreditBanner.css';

interface CreditBannerProps {
  /** Current credit state from useWallet. */
  creditState: CreditState;
  /** Remaining percentage from walletData; shown in the 'low' state. */
  remainingPercentage: number | null;
  /** Called when the "View usage" button is clicked. */
  onViewUsage: () => void;
  /**
   * When true, the banner is hidden via visibility:hidden but still occupies
   * its layout space — prevents the footer from reflowing and causing the
   * chat body to flicker on focus/blur.
   */
  hidden?: boolean;
}

/**
 * Displays a warning strip above the chat input when credit is low or exhausted.
 *
 * - 'low':       amber warning icon + "{N}% of AI credits remaining. View usage"
 * - 'exhausted': red error icon + "Out of AI credits. View usage"
 * - All other states: renders nothing (loading / healthy / error return null —
 *   no space reserved since there is never a banner in healthy state)
 */
export function CreditBanner({
  creditState,
  remainingPercentage,
  onViewUsage,
  hidden = false,
}: Readonly<CreditBannerProps>) {
  if (creditState !== 'low' && creditState !== 'exhausted') {
    return null;
  }

  const style = hidden ? { visibility: 'hidden' as const } : undefined;

  if (creditState === 'exhausted') {
    return (
      <div
        className="porter-credit-banner porter-credit-banner--exhausted"
        role="alert"
        aria-live="assertive"
        aria-hidden={hidden}
        style={style}
      >
        <IressIcon
          name="cancel"
          className="porter-credit-banner__icon"
          aria-hidden="true"
        />
        <span>Out of AI credits.</span>
        <button
          type="button"
          className="porter-credit-banner__link"
          onClick={onViewUsage}
          tabIndex={hidden ? -1 : undefined}
        >
          View usage
        </button>
      </div>
    );
  }

  // creditState === 'low'
  return (
    <div
      className="porter-credit-banner porter-credit-banner--low"
      role="alert"
      aria-live="polite"
      aria-hidden={hidden}
      style={style}
    >
      <IressIcon
        name="info"
        className="porter-credit-banner__icon"
        aria-hidden="true"
      />
      <span>
        {remainingPercentage ?? '—'}% of AI
        credits remaining.
      </span>
      <button
        type="button"
        className="porter-credit-banner__link"
        onClick={onViewUsage}
        tabIndex={hidden ? -1 : undefined}
      >
        View usage
      </button>
    </div>
  );
}
